import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { setLoading } from "../../../features/popupSlice";
import { getCustomHeader } from "../../../utils/requests";
import { useComponentSelection } from "./hooks/useComponentSelection";
import { useHeaderState } from "./hooks/useHeaderState";
import { usePublishCode } from "./hooks/usePublishCode";
import { useSidebar } from "./hooks/useSidebar";
import { useViewportSize } from "./hooks/useViewportSize";

import ImportModal from "./components/modals/ImportModal";
import PreviewModal from "./components/modals/PreviewModal";
import PublishModal from "./components/modals/PublishModal";
import PreviewContainer from "./components/ui/PreviewContainer";
import Sidebar from "./components/ui/Sidebar";
import Toolbar from "./components/ui/Toolbar";

import ComponentsPanel from "./components/panels/ComponentsPanel";
import SettingsPanel from "./components/panels/SettingsPanel";
import StylesPanel from "./components/panels/StylesPanel";
import HeaderBuilderRenderer from "./layouts/HeaderBuilderRenderer";

const HeaderDesignBuilder: React.FC = () => {
	// Get header ID from route params
	const { id: headerId } = useParams<{ id: string }>();
	const dispatch = useDispatch();

	// State management
	const { state, dispatch: headerDispatch } = useHeaderState();
	const {
		selectedComponentId,
		setSelectedComponentId,
		handleComponentClick,
		clearSelection,
	} = useComponentSelection();
	const { viewportSize, setViewportSize, getPreviewContainerStyle } =
		useViewportSize();
	const {
		sidebarOpen,
		setSidebarOpen,
		activeSidebarTab,
		setActiveSidebarTab,
		activeStyleTab,
		setActiveStyleTab,
	} = useSidebar();
	const {
		showPublishModal,
		setShowPublishModal,
		showPreviewModal,
		setShowPreviewModal,
		publishedCode,
		handlePublish,
		handlePreview,
	} = usePublishCode(state);

	// Add state for import modal
	const [showImportModal, setShowImportModal] = useState(false);

	// Fetch header data when component mounts or headerId changes
	useEffect(() => {
		if (headerId) {
			dispatch(setLoading(true));

			getCustomHeader(headerId)
				.then((res) => {
					if (res.data?.settings?.headerState) {
						headerDispatch({
							type: "SET_HEADER_STATE",
							payload: res.data?.settings?.headerState,
						});
					}
				})
				.catch((error) => {
					console.error("Error fetching header:", error);
				})
				.finally(() => {
					dispatch(setLoading(false));
				});
		}
	}, [headerId, dispatch, headerDispatch]);

	// Event handlers
	const handleComponentRemove = (id: string) => {
		headerDispatch({ type: "REMOVE_COMPONENT", payload: id });
		if (selectedComponentId === id) {
			clearSelection();
		}
	};

	const handlePreviewClick = () => {
		clearSelection();
		setActiveSidebarTab("settings");
	};

	// Add handler for import button
	const handleImport = () => {
		setShowImportModal(true);
	};

	const renderActiveSidebarContent = () => {
		switch (activeSidebarTab) {
			case "settings":
				return (
					<SettingsPanel
						selectedComponentId={selectedComponentId}
						setSelectedComponentId={setSelectedComponentId}
						state={state}
						dispatch={headerDispatch}
					/>
				);
			case "components":
				return <ComponentsPanel dispatch={headerDispatch} />;
			case "styles":
				return (
					<StylesPanel
						selectedComponentId={selectedComponentId}
						state={state}
						dispatch={headerDispatch}
						activeStyleTab={activeStyleTab}
						setActiveStyleTab={setActiveStyleTab}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div className="flex h-screen bg-gray-100">
			{/* Top Toolbar */}
			<Toolbar
				viewportSize={viewportSize}
				setViewportSize={setViewportSize}
				canUndo={state.currentHistoryIndex > 0}
				canRedo={state.currentHistoryIndex < state.history.length - 1}
				onUndo={() => headerDispatch({ type: "UNDO" })}
				onRedo={() => headerDispatch({ type: "REDO" })}
				onPublish={handlePublish}
				onPreview={handlePreview}
				onImport={handleImport}
			/>

			{/* Main Content Area */}
			<div className="flex-grow pt-14">
				{/* Header Preview */}
				<PreviewContainer
					viewportSize={viewportSize}
					getPreviewContainerStyle={getPreviewContainerStyle}
					onClick={handlePreviewClick}>
					{/* <HeaderLayout
                        state={state}
                        viewportSize={viewportSize}
                        selectedComponentId={selectedComponentId}
                        onComponentClick={handleComponentClick}
                        onComponentRemove={handleComponentRemove}
                    /> */}
					<HeaderBuilderRenderer
						state={state}
						viewportSize={viewportSize}
						selectedComponentId={selectedComponentId}
						onComponentClick={handleComponentClick}
						onComponentRemove={handleComponentRemove}
					/>
				</PreviewContainer>
			</div>

			{/* Sidebar */}
			<Sidebar
				sidebarOpen={sidebarOpen}
				setSidebarOpen={setSidebarOpen}
				activeSidebarTab={activeSidebarTab}
				setActiveSidebarTab={setActiveSidebarTab}
				activeStyleTab={activeStyleTab}
				setActiveStyleTab={setActiveStyleTab}
				selectedComponentId={selectedComponentId}
				state={state}
				dispatch={headerDispatch}
				setSelectedComponentId={setSelectedComponentId}>
				{renderActiveSidebarContent()}
			</Sidebar>

			{/* Publish Modal */}
			{showPublishModal && (
				<PublishModal
					publishedCode={publishedCode}
					onClose={() => setShowPublishModal(false)}
				/>
			)}

			{/* Preview Modal */}
			{showPreviewModal && (
				<PreviewModal
					state={state}
					onClose={() => setShowPreviewModal(false)}
				/>
			)}

			{/* Import Modal */}
			{showImportModal && (
				<ImportModal
					dispatch={headerDispatch}
					onClose={() => setShowImportModal(false)}
				/>
			)}
		</div>
	);
};

export default HeaderDesignBuilder;
